import { BarsOutlined, DashboardOutlined, DatabaseOutlined, LogoutOutlined } from '@ant-design/icons';
import type { RefineThemedLayoutSiderProps } from '@refinedev/antd';
import {
  CanAccess,
  ITreeMenu,
  pickNotDeprecated,
  useActiveAuthProvider,
  useIsExistAuthentication,
  useLink,
  useLogout,
  useMenu,
  useRefineContext,
  useRouterContext,
  useRouterType,
  useTranslate,
  useWarnAboutChange,
} from '@refinedev/core';
import { Badge, Button, Drawer, Grid, Layout, Menu, Row, theme } from 'antd';
import Typography from 'antd/lib/typography/Typography';
import React, { CSSProperties, FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { httpClient, ROOT_URL } from '../../App';
import { ADMIN_RESOURCE_NAMES, EXCLUSIVE_CLIENT_RESOURCES, RESOURCES } from '../../App.constants';
import { TOKEN_KEY } from '../../components/providers/authProvider';
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook';
import { getSocketManager } from '../../utils/socketManager';
// import { useOnfidoApplicant } from '../../utils/hooks/useOnfidoApplicant.hook';
// import { ReactComponent as UserNotVerifiedIcon } from '../../common/images/svg/user-not-verified.svg';
// import { ReactComponent as UserVerifiedIcon } from '../../common/images/svg/user-verified.svg';
// import { useOnfidoWorkflow } from '../../utils/hooks/useOnfidoWorkflow.hook';
// import { runNewWorkflow } from '../../api/onfido';
import worldCheckLogo from '../../common/images/png/world-check.png';
import { ReactComponent as SolutionErrorIcon } from '../../common/images/svg/solution-error.svg';
import { ReactComponent as SolutionSuccessIcon } from '../../common/images/svg/solution-success.svg';
import { ReactComponent as SolutionWarningIcon } from '../../common/images/svg/solution-warning.svg';
import { useWorldCheckVerification, VerificationStatus } from '../../utils/hooks/useWorldCheckVerification.hook';
import { WorldcheckVerificationModal } from '../modal/admin/worldCheckVerificationModal';

const drawerButtonStyles: CSSProperties = {
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  position: 'fixed',
  top: 64,
  zIndex: 999,
};

const { SubMenu } = Menu;
const { useToken } = theme;

const badgeCountStyle: CSSProperties = {
  flexShrink: 0,
  minWidth: 20,
  height: 20,
  lineHeight: '20px',
  fontSize: 12,
  padding: '0 6px',
  borderRadius: 10,
};

const SmartTitle: FC<{ isAdminRole: boolean }> = memo(({ isAdminRole }) => (
  <Typography
    style={{
      color: '#fff',
      fontFamily: 'Roboto',
      fontWeight: '500',
      fontSize: '24px',
      lineHeight: '32px',
      whiteSpace: 'nowrap',
    }}
  >
    RHYNEX V2 {isAdminRole ? '- ADMIN' : ''}
  </Typography>
));
SmartTitle.displayName = 'SmartTitle';

export const ThemedSider: React.FC<RefineThemedLayoutSiderProps> = ({ render, meta }) => {
  const { token } = useToken();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const isExistAuthentication = useIsExistAuthentication();
  const routerType = useRouterType();
  const NewLink = useLink();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const { Link: LegacyLink } = useRouterContext();
  const Link = routerType === 'legacy' ? LegacyLink : NewLink;
  const translate = useTranslate();
  const location = useLocation();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu({ meta });
  const breakpoint = Grid.useBreakpoint();

  // Override selectedKey when resource has parentName in meta
  const finalSelectedKey = useMemo(() => {
    // Find the resource that matches the current pathname
    const currentResource = RESOURCES.find(resource => {
      if (!resource.list) return false;
      const listPath = typeof resource.list === 'string' ? resource.list : null;
      return listPath && location.pathname.startsWith(listPath);
    });

    // Check if the resource has parentName in meta
    if (currentResource?.meta?.parentName) {
      const parentName = currentResource.meta.parentName;

      // Find the parent menu item key
      const findParentKey = (items: ITreeMenu[]): string | undefined => {
        for (const item of items) {
          if (item.name === parentName) {
            return item.key;
          }
          if (item.children && item.children.length > 0) {
            const found = findParentKey(item.children);
            if (found) return found;
          }
        }
        return undefined;
      };

      const parentKey = findParentKey(menuItems);
      if (parentKey) {
        return parentKey;
      }
    }

    return selectedKey;
  }, [location.pathname, menuItems, selectedKey]);
  const { hasDashboard } = useRefineContext();
  const authProvider = useActiveAuthProvider();
  const { mutate: mutateLogout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });
  const isAdminRole = useIsAdminRole();
  const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg;
  const [unreadPaymentRequestsCount, setUnreadPaymentRequestsCount] = useState<number>(0);
  const [unreadPaymentOrdersCount, setUnreadPaymentOrdersCount] = useState<number>(0);
  const [unreadExchangeCount, setUnreadExchangeCount] = useState<number>(0);
  const [unreadWithdrawalCount, setUnreadWithdrawalCount] = useState<number>(0);
  const [unreadInvoicePayablesCount, setUnreadInvoicePayablesCount] = useState<number>(0);
  const [unreadInvoiceReceivablesCount, setUnreadInvoiceReceivablesCount] = useState<number>(0);

  // Use socket manager to manage socket
  const socketManager = getSocketManager(ROOT_URL);

  // const {
  //   data: onfidoApplicant,
  //   fetch: fetchOnfidoApplicant,
  //   isLoading: onfidoApplicantLoading,
  // } = useOnfidoApplicant();
  // const { data: onfidoWorkflow, fetch: fetchOnfidoWorkflow, isLoading: onfidoWorkflowLoading } = useOnfidoWorkflow();
  const { data: worldCheckVerification, fetch: fetchWorldCheckVerificationData } = useWorldCheckVerification();
  const [worldcheckStatusModalOpened, setWorldcheckStatusModalOpened] = useState<boolean>(false);

  const updateUnreadRequestCount = useCallback(async () => {
    let count = 0;
    try {
      const { data } = await httpClient.get(`${ROOT_URL}/api/v1/payment_requests/not-viewed`);
      count = data;
    } catch (e) {
      console.log(e);
    }
    setUnreadPaymentRequestsCount(count);
  }, []);

  const updateUnreadOrderCount = useCallback(async () => {
    let count = 0;
    try {
      const { data } = await httpClient.get(`${ROOT_URL}/api/v1/payment_orders/not-viewed`);
      count = data;
    } catch (e) {
      console.log(e);
    }
    setUnreadPaymentOrdersCount(count);
  }, []);

  const updateUnreadExchangeCount = useCallback(async () => {
    let count = 0;
    try {
      const { data } = await httpClient.get(`${ROOT_URL}/api/v1/exchange_money_requests/not-viewed`);
      count = data;
    } catch (e) {
      console.log(e);
    }
    setUnreadExchangeCount(count);
  }, []);

  const updateUnreadWithdrawalCount = useCallback(async () => {
    let count = 0;
    try {
      const { data } = await httpClient.get(`${ROOT_URL}/api/v1/withdrawal-requests/not-viewed`);
      count = data;
    } catch (error) {
      console.error('Error updating withdrawal count:', error);
    }
    setUnreadWithdrawalCount(count);
  }, []);

  const updateUnreadInvoicePayablesCount = useCallback(async () => {
    let count = 0;
    try {
      const { data } = await httpClient.get(`${ROOT_URL}/api/v1/invoice_payables/not-viewed`);
      count = data;
    } catch (error) {
      console.error('Error updating invoice payables count:', error);
    }
    setUnreadInvoicePayablesCount(count);
  }, []);

  const updateUnreadInvoiceReceivablesCount = useCallback(async () => {
    let count = 0;
    try {
      const { data } = await httpClient.get(`${ROOT_URL}/api/v1/invoice_receivables/not-viewed`);
      count = data;
    } catch (error) {
      console.error('Error updating invoice receivables count:', error);
    }
    setUnreadInvoiceReceivablesCount(count);
  }, []);

  // useEffect(() => {
  //   (async () => {
  //     await fetchOnfidoApplicant();
  //   })();
  // }, [fetchOnfidoApplicant]);

  // useEffect(() => {
  //   if (!onfidoApplicant?.validated && onfidoApplicant?.workflowId) {
  //     (async () => {
  //       await fetchOnfidoWorkflow();
  //     })();
  //   }
  // }, [onfidoApplicant, fetchOnfidoWorkflow]);

  useEffect(() => {
    if (isAdminRole) {
      (async () => {
        await updateUnreadRequestCount();
        await updateUnreadOrderCount();
        await updateUnreadExchangeCount();
        await updateUnreadWithdrawalCount();
        await updateUnreadInvoicePayablesCount();
        await updateUnreadInvoiceReceivablesCount();
      })();
    }
  }, [
    updateUnreadRequestCount,
    updateUnreadOrderCount,
    updateUnreadExchangeCount,
    updateUnreadWithdrawalCount,
    updateUnreadInvoicePayablesCount,
    updateUnreadInvoiceReceivablesCount,
    isAdminRole,
  ]);

  // Initialize socket with token when component mounts and isAdminRole = true
  useEffect(() => {
    if (isAdminRole) {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        socketManager.initialize(token);
        socketManager.connect();
      }

      return () => {
        socketManager.disconnect();
      };
    }
  }, [isAdminRole, socketManager]);

  // Register event listeners (will be saved and automatically restored on reconnect)
  useEffect(() => {
    if (isAdminRole) {
      socketManager.on('paymentRequests.updated', updateUnreadRequestCount);
      socketManager.on('paymentOrders.updated', updateUnreadOrderCount);

      return () => {
        socketManager.off('paymentRequests.updated', updateUnreadRequestCount);
        socketManager.off('paymentOrders.updated', updateUnreadOrderCount);
      };
    }
  }, [socketManager, updateUnreadRequestCount, updateUnreadOrderCount, updateUnreadExchangeCount, isAdminRole]);

  // Re-register socket listeners when socket token is refreshed
  useEffect(() => {
    if (!isAdminRole) return;

    const handleSocketTokenRefreshed = () => {
      // Clear old listeners and register again to ensure they are bound
      socketManager.off('paymentRequests.updated', updateUnreadRequestCount);
      socketManager.off('paymentOrders.updated', updateUnreadOrderCount);

      socketManager.on('paymentRequests.updated', updateUnreadRequestCount);
      socketManager.on('paymentOrders.updated', updateUnreadOrderCount);
    };

    window.addEventListener('socketTokenRefreshed', handleSocketTokenRefreshed);

    return () => {
      window.removeEventListener('socketTokenRefreshed', handleSocketTokenRefreshed);
    };
  }, [isAdminRole, socketManager, updateUnreadRequestCount, updateUnreadOrderCount, updateUnreadExchangeCount]);

  useEffect(() => {
    if (isAdminRole) {
      const handleUpdateUnreadCounts = () => {
        updateUnreadRequestCount();
        updateUnreadOrderCount();
        updateUnreadExchangeCount();
        updateUnreadWithdrawalCount();
        updateUnreadInvoicePayablesCount();
        updateUnreadInvoiceReceivablesCount();
      };

      window.addEventListener('updateUnreadCounts', handleUpdateUnreadCounts);

      return () => {
        window.removeEventListener('updateUnreadCounts', handleUpdateUnreadCounts);
      };
    }
  }, [
    isAdminRole,
    updateUnreadRequestCount,
    updateUnreadOrderCount,
    updateUnreadExchangeCount,
    updateUnreadWithdrawalCount,
    updateUnreadInvoicePayablesCount,
    updateUnreadInvoiceReceivablesCount,
  ]);

  function formatLabel(label: string) {
    const count = label === 'Send Money' ? unreadPaymentRequestsCount : unreadPaymentOrdersCount;
    switch (label) {
      case 'Send Money':
      case 'Request Money':
        return (
          <Row justify='space-between' align='middle' wrap={false} style={{ gap: 4 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            <Badge style={{ ...badgeCountStyle }} count={count} overflowCount={99} />
          </Row>
        );
      case 'Exchange Money':
        return (
          <Row justify='space-between' align='middle' wrap={false} style={{ gap: 4 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            <Badge style={{ ...badgeCountStyle }} count={unreadExchangeCount} overflowCount={99} />
          </Row>
        );
      case 'Crypto Withdrawal':
        return (
          <Row justify='space-between' align='middle' wrap={false} style={{ gap: 4 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            <Badge style={{ ...badgeCountStyle }} count={unreadWithdrawalCount} overflowCount={99} />
          </Row>
        );
      case 'Invoices (Payables)':
        return (
          <Row justify='space-between' align='middle' wrap={false} style={{ gap: 4 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            <Badge style={{ ...badgeCountStyle }} count={unreadInvoicePayablesCount} overflowCount={99} />
          </Row>
        );
      case 'Invoices (Receivables)':
        return (
          <Row justify='space-between' align='middle' wrap={false} style={{ gap: 4 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            <Badge style={{ ...badgeCountStyle }} count={unreadInvoiceReceivablesCount} overflowCount={99} />
          </Row>
        );
      default:
        return label;
    }
  }

  const renderTreeView = (tree: ITreeMenu[], selectedKey?: string) => {
    return tree
      .filter(({ name }) =>
        isAdminRole ? !EXCLUSIVE_CLIENT_RESOURCES.includes(name) : !ADMIN_RESOURCE_NAMES.includes(name),
      )
      .map((item: ITreeMenu) => {
        const { icon, label: parentLabel, route, key, name, children, parentName, meta, options } = item;

        const label = meta?.label ? formatLabel(meta.label) : parentLabel;

        if (children.length > 0) {
          return (
            <CanAccess
              key={item.key}
              resource={name.toLowerCase()}
              action='list'
              params={{
                resource: item,
              }}
            >
              <SubMenu key={item.key} icon={icon ?? <DatabaseOutlined />} title={label}>
                {renderTreeView(children, selectedKey)}
              </SubMenu>
            </CanAccess>
          );
        }
        const isSelected = key === selectedKey;
        const isRoute = !(
          pickNotDeprecated(meta?.parent, options?.parent, parentName) !== undefined && children.length === 0
        );

        return (
          <CanAccess
            key={item.key}
            resource={name.toLowerCase()}
            action='list'
            params={{
              resource: item,
            }}
          >
            <Menu.Item key={item.key} icon={icon ?? (isRoute && <DatabaseOutlined />)}>
              <Link to={route ?? ''}>{label}</Link>
              {!collapsed && isSelected && <div className='ant-menu-tree-arrow' />}
            </Menu.Item>
          </CanAccess>
        );
      });
  };

  const handleLogout = () => {
    if (warnWhen) {
      const confirm = window.confirm(
        translate('warnWhenUnsavedChanges', 'Are you sure you want to leave? You have unsaved changes.'),
      );

      if (confirm) {
        setWarnWhen(false);
        mutateLogout();
      }
    } else {
      mutateLogout();
    }
  };

  const logout = isExistAuthentication && (
    <Menu.Item key='logout' onClick={() => handleLogout()} icon={<LogoutOutlined />} style={{ marginTop: '5vh' }}>
      {translate('buttons.logout', 'Logout')}
    </Menu.Item>
  );

  const dashboard = hasDashboard ? (
    <Menu.Item key='dashboard' icon={<DashboardOutlined />}>
      <Link to='/'>{translate('dashboard.title', 'Dashboard')}</Link>
      {!collapsed && selectedKey === '/' && <div className='ant-menu-tree-arrow' />}
    </Menu.Item>
  ) : null;

  const items = renderTreeView(menuItems, finalSelectedKey);

  const renderSider = () => {
    if (render) {
      return render({
        dashboard,
        items,
        logout,
        collapsed,
      });
    }

    return (
      <>
        {dashboard}
        {items}
      </>
    );
  };

  const renderMenu = () => {
    return (
      <>
        <Menu
          selectedKeys={finalSelectedKey ? [finalSelectedKey] : []}
          defaultOpenKeys={defaultOpenKeys}
          mode='inline'
          style={{
            marginTop: '8px',
            border: 'none',
          }}
          onClick={() => {
            setDrawerOpen(false);
            if (!breakpoint.lg) {
              setCollapsed(true);
            }
          }}
        >
          {renderSider()}
          {!isAdminRole && renderWorldcheckVerificationStatus()}
          {/* {renderOnfidoVerificationStatus()} */}
        </Menu>
      </>
    );
  };

  const renderDrawerSider = () => {
    return (
      <>
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement='left'
          closable={false}
          width={220}
          bodyStyle={{
            padding: 0,
          }}
          maskClosable={true}
        >
          <Layout>
            <Layout.Sider
              style={{
                height: '100vh',
                overflow: 'hidden',
                backgroundColor: token.colorBgContainer,
              }}
              width={220}
            >
              <div
                style={{
                  width: '220px',
                  padding: '0 0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '48px',
                  backgroundColor: '#001529',
                }}
              >
                {collapsed && <SmartTitle isAdminRole={isAdminRole} />}
              </div>
              {renderMenu()}
              {renderWorldcheckVerificationStatus()}
              <img src={worldCheckLogo} width='100%' />
              {/* {renderOnfidoVerificationStatus()} */}
            </Layout.Sider>
          </Layout>
        </Drawer>
        <Button
          style={drawerButtonStyles}
          size='large'
          onClick={() => setDrawerOpen(true)}
          icon={<BarsOutlined />}
        ></Button>
      </>
    );
  };

  // async function handleOnfidoVerification() {
  //   let page: Window | null = null;

  //   if (!onfidoWorkflow || onfidoWorkflow.status === 'declined') {
  //     const workflow = await runNewWorkflow();
  //     page = window.open(workflow.link.url, '_blank');
  //   } else if (onfidoWorkflow?.link?.url) {
  //     page = window.open(onfidoWorkflow.link.url, '_blank');
  //   }

  //   if (page) {
  //     const interval = setInterval(async () => {
  //       if (page?.closed) {
  //         clearInterval(interval);
  //         await fetchOnfidoWorkflow();
  //       }
  //     }, 250);
  //   }
  // }

  function renderWorldcheckVerificationStatus() {
    let text;
    let icon;

    switch (worldCheckVerification?.status) {
      case VerificationStatus.REQUIRED:
        icon = <SolutionWarningIcon />;
        text = (
          <div>
            <Typography>Not verified.</Typography>
            <Typography style={{ opacity: 0.6 }}>
              Company verification
              <br /> required
            </Typography>
          </div>
        );
        break;
      case VerificationStatus.PENDING:
        icon = <SolutionWarningIcon />;
        text = (
          <div>
            <Typography>Verification in progress</Typography>
            <Typography style={{ opacity: 0.6 }}>
              Company verification
              <br /> required
            </Typography>
          </div>
        );
        break;
      case VerificationStatus.LOW_RISK:
        icon = <SolutionSuccessIcon />;
        text = (
          <div>
            <Typography style={{ color: 'green' }}>Low risk.</Typography>
            <Typography style={{ opacity: 0.6 }}>
              Your company has passed
              <br /> verification, all the
              <br /> features are unlocked
            </Typography>
          </div>
        );
        break;
      case VerificationStatus.MEDIUM_RISK:
        icon = <SolutionWarningIcon />;
        text = (
          <div>
            <Typography style={{ color: '#faad14' }}>Medium risk.</Typography>
            <Typography style={{ opacity: 0.6 }}>
              Your company is verified,
              <br /> but might be blocked later.
            </Typography>
          </div>
        );
        break;
      case VerificationStatus.HIGH_RISK_EDD:
        icon = <SolutionErrorIcon />;
        text = (
          <div>
            <Typography style={{ color: 'red' }}>High risk (EDD)</Typography>
            <Typography style={{ opacity: 0.6 }}>
              Your company is at high
              <br /> risk
            </Typography>
          </div>
        );
        break;
      case VerificationStatus.HIGH_RISK_FIR:
        icon = <SolutionErrorIcon />;
        text = (
          <div>
            <Typography style={{ color: 'red' }}>High risk (FIR)</Typography>
            <Typography style={{ opacity: 0.6 }}>
              Your company is at high
              <br /> risk, no features are
              <br /> available.
            </Typography>
          </div>
        );
    }

    return (
      <>
        <div
          style={{
            position: 'fixed',
            bottom: 64,
            display: 'flex',
            marginLeft: 16,
            alignItems: 'flex-start',
            cursor: 'pointer',
          }}
          onClick={() => setWorldcheckStatusModalOpened(true)}
        >
          {icon}
          <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 10 }}>{text}</div>
        </div>
        <img src={worldCheckLogo} style={{ width: '100%', position: 'absolute', bottom: 0 }} />
      </>
    );
  }

  // const renderOnfidoVerificationStatus = () => {
  //   if (!onfidoApplicant || onfidoApplicantLoading || onfidoWorkflowLoading) {
  //     return null;
  //   }

  //   const verificationRequired = onfidoApplicant.onfidoVerificationRequired;
  //   let text: ReactNode | string = verificationRequired ? (
  //     <Typography>Verification is required</Typography>
  //   ) : (
  //     <Typography>Your account is verified</Typography>
  //   );
  //   const icon = verificationRequired ? <UserNotVerifiedIcon /> : <UserVerifiedIcon />;

  //   if (!verificationRequired) {
  //     return (
  //       <div style={{ position: 'fixed', bottom: 10, display: 'flex', marginLeft: 16, alignItems: 'center' }}>
  //         {icon}
  //         <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 10 }}>
  //           <Typography>{text}</Typography>
  //         </div>
  //       </div>
  //     );
  //   }

  //   let buttonText = 'Get started';
  //   const awaitsManualDecision =
  //     onfidoWorkflow?.status !== 'declined' && onfidoWorkflow?.tags?.includes('Manual decision');

  //   if (onfidoWorkflow) {
  //     if (awaitsManualDecision) {
  //       text = (
  //         <Typography>
  //           Awaiting manual review.
  //           <br /> Contact administrator at
  //           <br /> <a href='mailto:compliance@altcapitalfund.com'>compliance@altcapitalfund.com</a>
  //         </Typography>
  //       );
  //     } else {
  //       switch (onfidoWorkflow.status) {
  //         case 'awaiting_input':
  //           buttonText = 'Continue';
  //           break;
  //         case 'declined':
  //           text = (
  //             <Typography style={{ maxWidth: '50%' }}>
  //               Your previous verification was declined. Please, try again.
  //             </Typography>
  //           );
  //           buttonText = 'Retry';
  //           break;
  //         default:
  //           break;
  //       }
  //     }
  //   }

  //   const button =
  //     verificationRequired && !awaitsManualDecision ? (
  //       <Button style={{ padding: 0, width: 'fit-content' }} type='link' onClick={handleOnfidoVerification}>
  //         {buttonText}
  //       </Button>
  //     ) : null;

  //   return (
  //     <div style={{ position: 'fixed', bottom: 10, display: 'flex', marginLeft: 16, alignItems: 'center' }}>
  //       {icon}
  //       <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 10 }}>
  //         {text}
  //         {button}
  //       </div>
  //     </div>
  //   );
  // };

  if (isMobile) {
    return renderDrawerSider();
  }

  return (
    <Layout.Sider
      style={{
        backgroundColor: token.colorBgContainer,
        borderRight: `1px solid ${token.colorBgElevated}`,
      }}
      collapsible
      collapsed={collapsed}
      onCollapse={collapsed => setCollapsed(collapsed)}
      collapsedWidth={80}
      breakpoint='lg'
      width='245'
      trigger={null}
    >
      <div
        style={{
          width: '245px',
          padding: collapsed ? '0' : '0 22px',
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-start',
          alignItems: 'center',
          height: '48px',
          backgroundColor: '#001529',
          fontSize: '14px',
          marginBottom: '45px',
        }}
      >
        {!collapsed && <SmartTitle isAdminRole={isAdminRole} />}
      </div>
      {renderMenu()}
      {worldcheckStatusModalOpened && (
        <WorldcheckVerificationModal
          onClose={() => setWorldcheckStatusModalOpened(false)}
          status={worldCheckVerification?.status}
          updateVerificationStatus={fetchWorldCheckVerificationData}
        />
      )}
    </Layout.Sider>
  );
};
